"use client";

import { Divider } from "@/components/core/Divider";
import { Alert } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { usePassportProvider } from "@/context";
import { AlertCircle } from "lucide-react";
import Link from "next/link";
import { BalanceItem } from "./balance-item";
import type { BalanceToken } from "./types";

interface BalanceListProps {
  tokens: BalanceToken[];
  isLoading: boolean;
  error: Error | null;
  className?: string;
}

export function BalanceList({
  tokens,
  isLoading,
  error,
  className,
}: BalanceListProps) {
  const { isLoggedIn } = usePassportProvider();

  const allTokens = tokens.sort((a, b) => {
    const aUsd = Number(
      a.fiatEquivalentPrice?.amount?.replace(/[^0-9.-]/g, "") || 0,
    );
    const bUsd = Number(
      b.fiatEquivalentPrice?.amount?.replace(/[^0-9.-]/g, "") || 0,
    );
    return bUsd - aUsd;
  });

  if (error) {
    return (
      <div className={className}>
        <Alert className="bg-red-500/10 border-red-500/20 text-red-400">
          <AlertCircle className="h-4 w-4" />
          <span className="text-xs">
            Failed to load balances. Please try again.
          </span>
        </Alert>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={`space-y-2 ${className}`}>
        {/* Top divider */}
        <div className="w-full h-px bg-white/20" />

        <div className="box-border flex flex-col gap-1 items-center justify-start overflow-clip pb-2 pt-1 rounded-[8px] w-full">
          {/* Loading items with dividers */}
          {Array.from({ length: 3 }, (_, index) => `skeleton-${index}`).map(
            (skeletonId, index) => (
              <div key={skeletonId} className="w-full">
                {index > 0 && <div className="w-full h-px bg-white/20 my-1" />}
                <div className="flex items-center justify-between py-2 px-4">
                  <div className="flex gap-3 items-center flex-1 min-w-0">
                    <Skeleton className="w-8 h-8 rounded-lg bg-white/10 flex-shrink-0" />
                    <div className="flex-1 space-y-1">
                      <Skeleton className="h-4 w-3/4 bg-white/10" />
                      <Skeleton className="h-3 w-full bg-white/10" />
                    </div>
                  </div>
                  <div className="px-4">
                    <Skeleton className="h-4 w-16 bg-white/10" />
                  </div>
                  <div className="space-y-1">
                    <Skeleton className="h-4 w-20 bg-white/10" />
                    <Skeleton className="h-3 w-12 bg-white/10 ml-auto" />
                  </div>
                </div>
              </div>
            ),
          )}
        </div>
      </div>
    );
  }

  if (allTokens.length === 0) {
    return (
      <div className={`${className}`}>
        <div className="box-border flex flex-col gap-1 items-center justify-start overflow-clip pb-2 pt-1 rounded-[8px] w-full">
          <Divider className="mb-1" />
          <div className="text-center py-8">
            <div className="text-white/40 text-xs">No tokens found</div>
          </div>
          <Divider className="mt-1" />
        </div>
      </div>
    );
  }

  return (
    <div className={`${className}`}>
      {/* Main container without backdrop blur */}
      <div className="box-border flex flex-col gap-1 items-center justify-start overflow-clip pb-2 pt-1 rounded-[8px] w-full">
        {/* Top divider */}
        <Divider className="mb-1" />
        <div className={!isLoggedIn ? "overflow-hidden w-full" : "w-full"}>
          <div className="w-full">
            {allTokens.map((token, index) => (
              <div
                key={`${token.contractAddress || token.symbol}-${index}`}
                className="w-full"
              >
                <div className="relative">
                  <BalanceItem token={token} />
                </div>
                {index < allTokens.length - 1 && <Divider className="my-1" />}
              </div>
            ))}
          </div>
        </div>
        {/* Bottom divider */}
        <Divider className="mt-1" />
      </div>

      {isLoggedIn && (
        <div className="flex justify-center pt-1">
          <Link
            href="/balance"
            className="text-white/60 hover:text-white text-xs underline transition-colors"
          >
            More Details
          </Link>
        </div>
      )}
    </div>
  );
}
